import { BaseMapper, Undefinable } from '@ps/utils';

import {
  HEALTH_CHECK_STATUS,
  HealthCheckKpi,
  MultiCountry,
  PROJECT_TYPE,
  ProjectType,
  SURVEY_STATUS,
  SurveyChildScore,
} from '@ps/survey-kernel';
import {
  CPI,
  GroupedChildSurveyRef,
  GroupedStats,
  ProjectStats,
  Survey,
  SurveyPace,
  SurveyStatusCount,
  TotalCost,
  TrackerStats,
} from '@ps/survey-utils';

import { HealthCheckKpiDto } from '../dtos/health-check.dto';
import {
  GroupedChildSurveyMetaDto,
  ParentGroupedStatsDto,
  TcChannelDto,
  TrackerStatsDto,
} from '../dtos/projects.dto';
import { SurveyCountResponseDto, SurveyDto } from '../dtos/survey.dto';
import { GetSurveyByIdDto } from '../dtos/get-survey-by-id.dto';
import { SurveyQualificationMapper } from './survey-qualification.mapper';
import { AdvancedTargetingQuotaMapper } from './advanced-targeting-quota.mapper';

export class SurveyMapper extends BaseMapper<SurveyDto, Survey> {
  private readonly _qualificationMapper = new SurveyQualificationMapper();
  private readonly _quotaMapper = new AdvancedTargetingQuotaMapper();

  public fromDto(dto: SurveyDto): Survey {
    const mc = this._mapMc(dto);
    const project = this._mapProject({
      project: dto.project,
      project_type: dto.project_type,
    });
    const surveyGrouping = dto.survey_grouping?.length
      ? dto.survey_grouping
      : undefined;
    const aida = this._mapAida(dto);
    const qbp = this._mapQbp(dto);

    return {
      id: dto.ps_survey_id,
      aida,
      qbp,
      status: dto.ps_survey_status,
      inProgress: dto.in_progress,
      completesRequired: dto.completes_required,
      fielded: dto.fielded,
      pace: this._mapPace(dto),
      avgCpi: this._averageCpi(dto),
      aCpi: dto.acpi,
      currentIr: dto.current_incidence,
      countryName: dto.locale.country_name,
      countryCode: dto.locale.country_code,
      languageCode: dto.locale.language_translate,
      languageName: dto.locale.language_name,
      buyerCompanyId: dto.buyer_company,
      earningsPerClick: dto.epc,
      title: dto.survey_title,
      companyName: dto.company_name,
      billingId: dto.billing_id,
      currencySymbol: dto.currency_symbol,
      mc,
      project,
      surveyGrouping,
      projectType: dto.project_type,
      mcUniqueId: dto.mc_unique_id,
      projectId: dto.ps_project_id,
      isParent: this._isParent(dto.project_type, mc, project, dto.tc_count),
      isQbpChild: dto.is_qbp_child_survey,
      isQbpEnabled: dto.is_qbp_enable,
      isAidaEnabled: dto.is_aida_enabled,
      isFeotEnabled: dto.is_feot_enabled,
      launchDate: dto.survey_launch_date,
      expectedLoi: dto.expected_loi,
      expectedIr: dto.expected_ir,
      fieldTime: dto.field_time,
      clickBalancing: dto.click_balancing,
      buyerPm: dto.buyer_pm,
      currentCost: dto.current_cost,
      reconciliation: dto.reconciliation,
      templateId: dto.template_id,
      tcCount: dto.tc_count,
      surveyScore: dto.survey_score,
      crmId: dto.crm_id,
      operatorPm: dto.operator_pm,
      marginBank: dto.margin_bank,
      actualMargin: dto.actual_margin,
      healthCheck: this._mapHealthCheck(dto.healthCheck),
      childScores: this._mapProjectChildScores(dto, mc, project),
      totalCost: this._mapTotalCost(dto),
      cpi: this._mapCpi(dto),
      isMarginOverrideEnabled: !!dto.is_enable_margin_override,
      isMarginMaxEnabled: !!dto.is_enable_margin_max,
      isManualScpiOverrideEnabled: !!dto.manual_scpi_override_enabled,
      marginOverride: dto.margin_override?.margin,
      createdAt: dto.creation_date,
      modifiedAt: dto.mod_on,
      qualifications: this._qualificationMapper.fromDtoList(
        dto.qualifications || [],
      ),
      quotas: this._quotaMapper.fromDtoList(dto.quotas || []),
      channelType: '',
      isPending: false,
    };
  }

  public fromCountResponseDto(
    response: SurveyCountResponseDto,
  ): SurveyStatusCount {
    return Object.fromEntries(
      response.total.map((item) => [item._id, item.count]),
    );
  }

  public fromDetailsDto(dto: GetSurveyByIdDto): Survey {
    return {
      ...this.fromDto(dto),
      aCpi: dto.acpi ?? dto.cpi?.current,
      currentCost: dto.total_cost?.current,
      buyerPm: dto.buyer_pm ?? dto.bpm?.nm,
      operatorPm: dto.operator_pm ?? dto.pm?.nm,
      recentIr: dto.incidence?.recent,
      recentLoi: dto.loi?.recent,
    };
  }

  private _mapPace(dto: SurveyDto): SurveyPace {
    return {
      expected: dto.pacing_data?.cumulative_pace.expected ?? 0,
      actual: dto.pacing_data?.cumulative_pace.actual ?? 0,
    };
  }

  private _mapTotalCost(dto: SurveyDto): TotalCost {
    return {
      current: dto.total_cost?.current,
      projected: dto.total_cost?.projected,
      launch: dto.total_cost?.launch,
    };
  }

  private _mapCpi(dto: SurveyDto): CPI {
    return {
      current: dto.cpi?.current,
      launch: dto.cpi?.launch,
      projected: dto.cpi?.projected,
      feasibilityCpi: dto.cpi?.feasibility_cpi,
    };
  }

  private _averageCpi(dto: SurveyDto): number {
    if (dto.ps_survey_status === SURVEY_STATUS.DRAFT) {
      return 0;
    }

    if (dto.fielded > 0) {
      return dto.average_cpi || 0;
    }

    return dto.acpi || 0;
  }

  private _mapMc(dto: SurveyDto): MultiCountry | undefined {
    if (dto.mc?.parent === undefined) {
      return undefined;
    }
    return {
      svp: !!dto.mc.SVP,
      parent: !!dto.mc.parent,
      surveys: (dto.mc.surveys ?? []).map((s) => ({
        surveyId: s.surveyId,
        countryCode: s.countryCode,
        languageCode: s.languageCode,
        healthCheck: this._mapHealthCheck(s.healthCheck),
        score: s.score,
        scorePercentage: s.scorePercentage,
      })),
    };
  }

  private _mapHealthCheck(
    kpis: HealthCheckKpiDto[] | undefined,
  ): HealthCheckKpi[] {
    if (!kpis?.length) {
      return [];
    }
    const validStatusTexts: readonly string[] =
      Object.keys(HEALTH_CHECK_STATUS);
    return kpis
      .filter(
        (kpi) =>
          typeof kpi.status === 'number' &&
          typeof kpi.name === 'string' &&
          validStatusTexts.includes(kpi.status_text),
      )
      .map((kpi) => ({
        status: kpi.status,
        statusText: kpi.status_text,
        name: kpi.name,
      }));
  }

  private _mapProject(
    dto: Pick<SurveyDto, 'project' | 'project_type'>,
  ): ProjectStats {
    if (!dto.project || !dto.project_type) {
      return undefined;
    }
    if (dto.project_type === PROJECT_TYPE.TRACKER) {
      return this._mapTrackerStats(dto.project as TrackerStatsDto);
    }
    if (
      dto.project_type === PROJECT_TYPE.GROUPED &&
      (dto.project as ParentGroupedStatsDto).parent
    ) {
      return this._mapGroupedStats(dto.project as ParentGroupedStatsDto);
    }
    return undefined;
  }

  private _mapTrackerStats(dto: TrackerStatsDto): TrackerStats {
    if (dto.parent) {
      return {
        parent: true,
        waveCount: dto.wave_count,
        exclusionRule: dto.exclusion_rule,
        frequency: dto.frequency,
        surveys: (dto.surveys ?? []).map((s) => ({
          surveyId: s.surveyId,
          waveId: s.wave_id,
          waveExclusions: s.wave_exclusions,
          status: s.status,
          score: s.score,
          scorePercentage: s.scorePercentage,
        })),
        counts: {
          totalDraft: dto.counts?.total_draft ?? 0,
          totalLaunched: dto.counts?.total_launched ?? 0,
        },
      };
    }
    return {
      parent: false,
      waveId: dto.wave_id,
      waveExclusions: dto.wave_exclusions,
    };
  }

  private _mapGroupedStats(dto: ParentGroupedStatsDto): GroupedStats {
    return {
      parent: true,
      surveys: (dto.surveys ?? []).map((s) => this._mapGroupedSurveyChild(s)),
      counts: {
        totalDraft: dto.counts?.total_draft ?? 0,
        totalLaunched: dto.counts?.total_launched ?? 0,
      },
    };
  }

  private _mapGroupedSurveyChild(
    dto: GroupedChildSurveyMetaDto,
  ): GroupedChildSurveyRef {
    const [languageCode, countryCode] = dto.survey_localization.split('_');
    return {
      surveyId: dto.surveyId,
      status: dto.status,
      title: dto.title,
      countryCode: countryCode ?? '',
      languageCode: languageCode ?? '',
      score: dto.score,
      scorePercentage: dto.scorePercentage,
    };
  }

  private _mapProjectChildScores(
    survey: SurveyDto,
    mc: Undefinable<MultiCountry>,
    project: Undefinable<TrackerStats | GroupedStats>,
  ): Undefinable<SurveyChildScore[]> {
    if (survey.project_type === PROJECT_TYPE.MCQB && mc?.parent === true) {
      return mc.surveys.map((childSurvey) =>
        this._mapSingleChildScore(childSurvey),
      );
    }
    if (
      survey.project_type === PROJECT_TYPE.TRACKER &&
      project?.parent === true
    ) {
      const trackerParent = project as Extract<TrackerStats, { parent: true }>;
      return trackerParent.surveys.map((childSurvey) =>
        this._mapSingleChildScore(childSurvey, {
          countryCode: survey.locale.country_code,
          languageCode: survey.locale.language_translate,
        }),
      );
    }
    if (
      survey.project_type === PROJECT_TYPE.GROUPED &&
      project?.parent === true
    ) {
      const groupedParent = project as Extract<GroupedStats, { parent: true }>;
      return groupedParent.surveys.map((childSurvey: GroupedChildSurveyRef) =>
        this._mapSingleChildScore(childSurvey),
      );
    }
    return undefined;
  }

  private _mapSingleChildScore(
    survey: {
      surveyId: number;
      countryCode?: string;
      languageCode?: string;
      score?: number | null;
      scorePercentage?: number | null;
    },
    localeOverride?: Pick<SurveyChildScore, 'countryCode' | 'languageCode'>,
  ): SurveyChildScore {
    return {
      surveyId: survey.surveyId,
      countryCode: localeOverride?.countryCode ?? survey.countryCode ?? '',
      languageCode: localeOverride?.languageCode ?? survey.languageCode ?? '',
      score: survey.score ?? 0,
      scorePercentage: survey.scorePercentage ?? 0,
    };
  }

  private _isParent(
    projectType: Undefinable<ProjectType>,
    mc: Undefinable<MultiCountry>,
    project: Undefinable<TrackerStats | GroupedStats>,
    tcCount?: number,
  ): boolean {
    if (projectType === PROJECT_TYPE.MCQB) return !!mc?.parent;
    if (
      projectType === PROJECT_TYPE.TRACKER ||
      projectType === PROJECT_TYPE.GROUPED
    )
      return !!project?.parent;
    return (tcCount ?? 0) > 0;
  }

  private _mapAida({ aida }: SurveyDto): Survey['aida'] {
    return {
      enabled: !!aida?.enabled,
      selectedQuotaIds: aida?.selected_quota_ids ?? [],
      category: aida?.category,
    };
  }

  private _mapQbp({ qbp }: SurveyDto): Survey['qbp'] {
    return {
      enable: !!qbp?.enable,
      category: qbp?.category,
      quotaIds: qbp?.quota_ids ?? [],
      selectedQuotaIds: qbp?.selectedQuotaIds ?? [],
    };
  }
}

const PENDING_CHANNEL_TITLES: Record<string, string> = {
  REP: 'REPLICA',
  UNT: 'UNTARGETED',
  SPA: 'SPECIFIC AUDIENCE',
  TL: 'TARGETED LIST',
  LZ: 'Large Zip',
  AP: 'Allocation Priority',
  SR: 'Stealth Replica',
};

const DEFAULT_PENDING_CHANNEL_TITLE = 'CHANNEL';

export class TcChannelMapper extends BaseMapper<TcChannelDto, Survey> {
  public fromDto(dto: TcChannelDto): Survey {
    if (dto.create_status_description === 'PENDING') {
      return this._fromPendingDto(dto);
    }
    return this._fromReadyDto(dto);
  }

  private _fromPendingDto(dto: TcChannelDto): Survey {
    return {
      ...this._defaults(),
      title:
        PENDING_CHANNEL_TITLES[dto.channel_type] ??
        DEFAULT_PENDING_CHANNEL_TITLE,
      channelType: dto.channel_type,
      childStatusId: dto.child_status_id,
      isPending: true,
    };
  }

  private _fromReadyDto(dto: TcChannelDto): Survey {
    return {
      ...this._defaults(),
      id: dto.survey_id ?? 0,
      status: dto.status ?? SURVEY_STATUS.DRAFT,
      inProgress: dto.in_progress ?? 0,
      completesRequired: dto.goal ?? 0,
      fielded: dto.fielded ?? 0,
      earningsPerClick: dto.epc ?? 0,
      title: dto.title ?? '',
      currencySymbol: dto.symbol ?? '',
      avgCpi: dto.avg_cpi,
      currentIr: dto.current_ir,
      launchDate: dto.launch_date,
      currentCost: dto.cpi_current ?? 0,
      totalStarts: dto.total_starts ?? 0,
      last24Starts: dto.last_24_starts ?? 0,
      channelType: dto.channel_type,
      isPending: false,
    };
  }

  private _defaults(): Survey {
    return {
      id: 0,
      status: SURVEY_STATUS.DRAFT,
      inProgress: 0,
      completesRequired: 0,
      fielded: 0,
      pace: { expected: 0, actual: 0 },
      earningsPerClick: 0,
      title: '',
      currencySymbol: '',
      currentCost: 0,
      totalStarts: 0,
      last24Starts: 0,
      channelType: '',
      isPending: false,
      countryName: '',
      countryCode: '',
      languageCode: '',
      companyName: '',
      billingId: '',
      buyerCompanyId: 0,
      expectedLoi: 0,
      expectedIr: 0,
      fieldTime: 0,
      clickBalancing: 0,
      isParent: false,
      isQbpChild: false,
      isQbpEnabled: false,
      isAidaEnabled: false,
      isFeotEnabled: false,
      healthCheck: [],
      isMarginOverrideEnabled: false,
      isMarginMaxEnabled: false,
      isManualScpiOverrideEnabled: false,
    };
  }
}
